import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import AppShell from "../layouts/AppShell";
import { GET, POST, PUT, DELETE, loading, alert, format_date, toFixed, required, profile, Select, DateInput } from "../components/CustomComponent";
import PdfEditor from "../components/pdf-editor/PdfEditor";
import { devContractSource } from "../components/pdf-editor/sources";

const API = "v1/admin/dev-contracts";
const PAGE_TITLE = "สัญญาพัฒนาระบบ";
const MAX_PDF_MB = 50;
const MAX_STAMP_MB = 5;

// หน้านี้เปิดให้เฉพาะบัญชี super_user (เมนูซ่อนให้แล้วใน Navbar และ backend กันด้วย checkSuperUser อีกชั้น)
const isSuperUser = profile?.user_type === "super_user";

/* ------------------------------------------------------------------ */
/* helpers                                                             */
/* ------------------------------------------------------------------ */

const showDate = (iso) => (iso ? format_date(iso, "dd/mm/yyyy") : "—");
const money = (value) => `฿${toFixed(Number(value) || 0)}`;

const formatBytes = (bytes) => {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return "—";
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const isPdfFile = (file) => file && (file.type === "application/pdf" || /\.pdf$/i.test(file.name || ""));
const isImageFile = (file) => file && (String(file.type || "").startsWith("image/") || /\.(png|jpe?g|webp|gif|bmp)$/i.test(file.name || ""));

// ภาพตราแปลงเป็น PNG เสมอ (ย่อให้ด้านยาวไม่เกินนี้ ตราใช้ขนาดเล็กบนหน้ากระดาษ ไม่จำเป็นต้องใหญ่กว่านี้)
const STAMP_MAX_SIDE = 1200;
// พิกเซลที่ค่าสีต่ำสุดใน RGB สูงกว่านี้ถือว่าเป็นพื้นขาว ให้โปร่งใสทั้งหมด
const WHITE_CUTOFF = 235;
// ช่วงรอยต่อ: ค่อย ๆ ไล่ความโปร่งจาก WHITE_SOFT -> WHITE_CUTOFF ให้ขอบตราไม่หยัก
const WHITE_SOFT = 180;

const loadImageElement = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("decode image failed"));
    img.src = src;
  });

// แปลงภาพตราทุกชนิดที่เบราว์เซอร์เปิดได้ (JPEG/WebP/PNG ฯลฯ) เป็น PNG และเลือกลบพื้นหลังสีขาวให้โปร่งได้
// ทำฝั่ง client ทั้งหมด backend รับแค่ PNG ที่แปลงแล้ว ไม่ต้องติดตั้งไลบรารีประมวลผลภาพบนเซิร์ฟเวอร์
const convertStampToPng = async (dataUrl, removeBackground) => {
  const img = await loadImageElement(dataUrl);
  const scale = Math.min(1, STAMP_MAX_SIDE / Math.max(img.naturalWidth || 1, img.naturalHeight || 1));
  const width = Math.max(1, Math.round((img.naturalWidth || 1) * scale));
  const height = Math.max(1, Math.round((img.naturalHeight || 1) * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, width, height);

  if (removeBackground) {
    const imageData = ctx.getImageData(0, 0, width, height);
    const px = imageData.data;
    for (let i = 0; i < px.length; i += 4) {
      const low = Math.min(px[i], px[i + 1], px[i + 2]);
      if (low >= WHITE_CUTOFF) px[i + 3] = 0;
      else if (low >= WHITE_SOFT) px[i + 3] = Math.round((px[i + 3] * (WHITE_CUTOFF - low)) / (WHITE_CUTOFF - WHITE_SOFT));
    }
    ctx.putImageData(imageData, 0, 0);
  }

  return canvas.toDataURL("image/png");
};

// ขนาดไฟล์จริง (byte) ของ data URL base64
const dataUrlBytes = (dataUrl) => {
  const base64 = String(dataUrl || "").split("base64,")[1] || "";
  const padding = base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0;
  return Math.max(0, Math.floor((base64.length * 3) / 4) - padding);
};

const STAMP_FILTER_OPTIONS = [
  { value: "stamped", label: "ประทับตราแล้ว" },
  { value: "pending", label: "รอประทับตรา" },
];

const sectionCardStyle = { background: "#ffffff", border: "1px solid rgba(18, 125, 255, 0.10)", borderRadius: 24, boxShadow: "0 14px 32px rgba(12, 118, 229, 0.06)" };

/* ---- ชิ้นส่วนฟอร์ม (รูปแบบเดียวกับหน้าสัญญา MA) ---- */
const FormSection = ({ step, title, hint, children }) => (
  <section className="dc-form-card">
    <header className="d-flex align-items-start gap-3 mb-3">
      <span className="dc-form-step">{step}</span>
      <div style={{ minWidth: 0 }}>
        <h6 className="mb-0 fw-bold" style={{ color: "#12314a", fontSize: 15, letterSpacing: "-0.01em" }}>{title}</h6>
        {hint ? <p className="mb-0" style={{ fontSize: 12, color: "#8296a8" }}>{hint}</p> : null}
      </div>
    </header>
    <div className="row g-3 g-md-4">{children}</div>
  </section>
);

const Field = ({ col = "col-md-4", label, hint, must, children }) => (
  <div className={col}>
    <label className="dc-label">
      {label}
      {must ? <span className="ms-1">{required}</span> : null}
    </label>
    {children}
    {hint ? <p className="dc-hint">{hint}</p> : null}
  </div>
);

// พื้นที่เลือกไฟล์ PDF: คลิกหรือลากไฟล์มาวาง รองรับเฉพาะ PDF
const PdfDropzone = ({ file, currentName, currentSize, onChange }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const pick = (picked) => {
    if (!picked) return;
    if (!isPdfFile(picked)) return alert("", "warning", "แจ้งเตือน", "รองรับเฉพาะไฟล์ PDF เท่านั้น");
    if (picked.size > MAX_PDF_MB * 1024 * 1024) return alert("", "warning", "แจ้งเตือน", `ไฟล์ PDF ต้องไม่เกิน ${MAX_PDF_MB}MB`);
    onChange(picked);
    return undefined;
  };

  return (
    <div
      className={`dc-dropzone${dragging ? " dc-dropzone-active" : ""}`}
      onClick={() => inputRef.current?.click()}
      onDragOver={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        pick(event.dataTransfer?.files?.[0]);
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => (event.key === "Enter" || event.key === " ") && inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="d-none"
        onChange={(event) => {
          pick(event.target.files?.[0]);
          event.target.value = "";
        }}
      />
      <i className="fa-solid fa-file-pdf" style={{ fontSize: 30, color: file ? "#dc2626" : "#94a3b8" }} />
      {file ? (
        <>
          <div className="fw-semibold text-truncate mt-2" style={{ color: "#132f46", maxWidth: "100%" }}>{file.name}</div>
          <div className="small text-secondary">{formatBytes(file.size)} · คลิกเพื่อเปลี่ยนไฟล์</div>
          {currentName ? <div className="small mt-1" style={{ color: "#b45309" }}>จะแทนที่ต้นฉบับเดิม และฉบับประทับตราเดิมจะถูกลบ</div> : null}
        </>
      ) : currentName ? (
        <>
          <div className="fw-semibold text-truncate mt-2" style={{ color: "#132f46", maxWidth: "100%" }}>{currentName}</div>
          <div className="small text-secondary">{formatBytes(currentSize)} · ต้นฉบับปัจจุบัน — คลิกหรือลากไฟล์ใหม่มาวางเพื่อแทนที่</div>
        </>
      ) : (
        <>
          <div className="fw-semibold mt-2" style={{ color: "#132f46" }}>คลิกเพื่อเลือกไฟล์ หรือลากไฟล์ PDF มาวางที่นี่</div>
          <div className="small text-secondary">นำเข้า PDF สัญญาต้นฉบับ (ไม่เกิน {MAX_PDF_MB}MB) ระบบจะเก็บต้นฉบับไว้โดยไม่แก้ไข</div>
        </>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* ฟอร์มสร้าง/แก้ไขสัญญา                                               */
/* ------------------------------------------------------------------ */

const emptyForm = {
  uid: "",
  project_name: "",
  customer_name: "",
  contract_no: "",
  contract_date: "",
  start_date: "",
  end_date: "",
  contract_amount: "",
  note: "",
};

const ContractModal = ({ show, onHide, editData, onSave }) => {
  const [form, setForm] = useState(emptyForm);
  const [file, setFile] = useState(null);

  useEffect(() => {
    if (!show) return;
    setFile(null);
    setForm(
      editData
        ? {
            uid: editData.uid || "",
            project_name: editData.project_name || "",
            customer_name: editData.customer_name || "",
            contract_no: editData.contract_no || "",
            contract_date: editData.contract_date || "",
            start_date: editData.start_date || "",
            end_date: editData.end_date || "",
            contract_amount: editData.contract_amount ?? "",
            note: editData.note || "",
          }
        : emptyForm
    );
  }, [show, editData]);

  const set = (key) => (eventOrValue) => {
    const value = eventOrValue?.target ? eventOrValue.target.value : eventOrValue;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <Modal show={show} onHide={onHide} centered size="xl" contentClassName="border-0 shadow-lg overflow-hidden dc-form-modal" dialogClassName="modal-dialog-scrollable">
      <Modal.Header closeButton className="border-0 px-4 px-md-5 pt-4 pb-4" style={{ background: "linear-gradient(135deg, #e8f3ff 0%, #f6fbff 100%)" }}>
        <div>
          <Modal.Title className="fw-bold mb-0" style={{ color: "#0d2a41", fontSize: "1.5rem", letterSpacing: "-0.02em" }}>
            {form.uid ? "แก้ไขสัญญาพัฒนาระบบ" : "นำเข้าสัญญาพัฒนาระบบ"}
          </Modal.Title>
          <p className="mb-0 mt-1" style={{ fontSize: 13, color: "#6b8198" }}>
            {form.uid ? "แก้ไขรายละเอียดสัญญา หรือเปลี่ยนไฟล์ PDF ต้นฉบับ" : "อัปโหลด PDF สัญญาต้นฉบับ แล้วไปประทับตราบริษัทได้ที่ปุ่ม “ประทับตรา” ในตาราง"}
          </p>
        </div>
      </Modal.Header>

      <Modal.Body className="px-3 px-md-5 py-4" style={{ background: "linear-gradient(180deg, #f7fbff 0%, #eef6fd 100%)" }}>
        <FormSection step="1" title="ไฟล์ PDF ต้นฉบับ" hint="ระบบเก็บต้นฉบับไว้ตามเดิมทุกหน้า การประทับตราจะสร้างเป็นไฟล์ใหม่แยกต่างหาก">
          <div className="col-12">
            <PdfDropzone file={file} currentName={editData?.original_file_name} currentSize={editData?.original_file_size} onChange={setFile} />
          </div>
        </FormSection>

        <FormSection step="2" title="รายละเอียดสัญญา" hint="ใช้ค้นหาและแยกแยะสัญญาในตาราง">
          <Field col="col-md-6" label="ชื่อโปรเจกต์ / ระบบ" must>
            <input type="text" className="form-control dc-input" value={form.project_name} onChange={set("project_name")} placeholder="เช่น ระบบบริหารงานซ่อมบำรุง" />
          </Field>
          <Field col="col-md-6" label="ลูกค้า / คู่สัญญา">
            <input type="text" className="form-control dc-input" value={form.customer_name} onChange={set("customer_name")} placeholder="ชื่อบริษัทหรือหน่วยงาน" />
          </Field>
          <Field label="เลขที่สัญญา">
            <input type="text" className="form-control dc-input" value={form.contract_no} onChange={set("contract_no")} placeholder="เช่น PT-2026-001" />
          </Field>
          <Field label="วันที่ทำสัญญา">
            <DateInput className="form-control dc-input" value={form.contract_date} onChange={set("contract_date")} />
          </Field>
          <Field label="มูลค่าสัญญา (บาท)">
            <input type="number" min="0" step="0.01" className="form-control dc-input" value={form.contract_amount} onChange={set("contract_amount")} placeholder="0.00" />
          </Field>
          <Field label="วันเริ่มงาน">
            <DateInput className="form-control dc-input" value={form.start_date} max={form.end_date || undefined} onChange={set("start_date")} />
          </Field>
          <Field label="วันส่งมอบ / สิ้นสุดสัญญา">
            <DateInput className="form-control dc-input" value={form.end_date} min={form.start_date || undefined} onChange={set("end_date")} />
          </Field>
          <Field col="col-12" label="หมายเหตุ">
            <textarea className="form-control dc-input" rows={3} value={form.note} onChange={set("note")} placeholder="บันทึกเพิ่มเติม เช่น เงื่อนไขพิเศษ งวดงาน" />
          </Field>
        </FormSection>
      </Modal.Body>

      <Modal.Footer className="border-0 px-4 px-md-5 py-3" style={{ background: "#ffffff", borderTop: "1px solid rgba(18, 125, 255, 0.12)" }}>
        <button type="button" className="btn btn-light px-4" style={{ borderRadius: 12, border: "1px solid rgba(18, 125, 255, 0.14)", color: "#4f6d83" }} onClick={onHide}>
          ยกเลิก
        </button>
        <button type="button" className="btn btn-primary px-4" style={{ borderRadius: 12 }} onClick={() => onSave(form, file)}>
          {form.uid ? "บันทึกการแก้ไข" : "นำเข้าสัญญา"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

/* ------------------------------------------------------------------ */
/* คลังภาพตราปั๊ม                                                      */
/* ------------------------------------------------------------------ */

const StampManagerModal = ({ show, onHide, stamps, stampImages, onChanged }) => {
  const [name, setName] = useState("");
  const [file, setFile] = useState(null); // ไฟล์ต้นทางที่ผู้ใช้เลือก (ชนิดใดก็ได้)
  const [source, setSource] = useState(""); // data URL ของไฟล์ต้นทาง
  const [preview, setPreview] = useState(""); // PNG ที่แปลงแล้ว = ไฟล์ที่จะอัปโหลดจริง
  const [removeBg, setRemoveBg] = useState(true);
  const [converting, setConverting] = useState(false);
  const inputRef = useRef(null);

  const reset = () => {
    setName("");
    setFile(null);
    setSource("");
    setPreview("");
    setRemoveBg(true);
  };

  useEffect(() => {
    if (!show) return;
    setName("");
    setFile(null);
    setSource("");
    setPreview("");
    setRemoveBg(true);
  }, [show]);

  // แปลงเป็น PNG ใหม่ทุกครั้งที่เปลี่ยนไฟล์หรือสลับตัวเลือกลบพื้นหลัง ให้เห็นผลลัพธ์จริงก่อนอัปโหลด
  useEffect(() => {
    if (!source) {
      setPreview("");
      return undefined;
    }
    let cancelled = false;
    setConverting(true);
    convertStampToPng(source, removeBg)
      .then((png) => {
        if (!cancelled) setPreview(png);
      })
      .catch(() => {
        if (cancelled) return;
        setPreview("");
        alert("", "warning", "แจ้งเตือน", "เปิดภาพนี้ไม่ได้ กรุณาเลือกไฟล์ภาพชนิดอื่น เช่น PNG หรือ JPEG");
      })
      .finally(() => {
        if (!cancelled) setConverting(false);
      });
    return () => {
      cancelled = true;
    };
  }, [source, removeBg]);

  const pickFile = async (picked) => {
    if (!picked) return;
    if (!isImageFile(picked)) return alert("", "warning", "แจ้งเตือน", "กรุณาเลือกไฟล์ภาพ (PNG, JPEG, WebP ฯลฯ) ระบบจะแปลงเป็น PNG ให้");
    if (picked.size > MAX_STAMP_MB * 1024 * 1024) return alert("", "warning", "แจ้งเตือน", `ภาพตราต้องไม่เกิน ${MAX_STAMP_MB}MB`);
    setFile(picked);
    setSource(await readFileAsDataUrl(picked));
    if (!name.trim()) setName(picked.name.replace(/\.[^.]+$/, ""));
    return undefined;
  };

  const handleUpload = async () => {
    if (!name.trim()) return alert("", "warning", "แจ้งเตือน", "กรุณาตั้งชื่อตรา");
    if (!file) return alert("", "warning", "แจ้งเตือน", "กรุณาเลือกภาพตรา");
    if (converting || !preview) return alert("", "warning", "แจ้งเตือน", "กำลังแปลงภาพเป็น PNG กรุณารอสักครู่");
    if (dataUrlBytes(preview) > MAX_STAMP_MB * 1024 * 1024) return alert("", "warning", "แจ้งเตือน", `ภาพ PNG ที่แปลงแล้วใหญ่เกิน ${MAX_STAMP_MB}MB กรุณาใช้ภาพที่เล็กลง`);
    loading(true);
    // อัปโหลด PNG ที่แปลงแล้วเสมอ ไม่ว่าไฟล์ต้นทางจะเป็นชนิดใด
    const result = await POST(`${API}/stamps`, {
      name: name.trim(),
      file_name: `${name.trim()}.png`,
      mime_type: "image/png",
      file_base64: preview,
      is_default: stamps.length === 0,
    });
    loading(false);
    if (result?.status) {
      reset();
      await onChanged();
    } else {
      await alert("", "warning", "แจ้งเตือน", result?.message || "อัปโหลดไม่สำเร็จ");
    }
    return undefined;
  };

  const handleSetDefault = async (stamp) => {
    if (stamp.is_default) return;
    loading(true);
    const result = await PUT(`${API}/stamps/${stamp.uid}`, { is_default: true }, null, { keepEmpty: true });
    loading(false);
    if (result?.status) await onChanged();
    else await alert("", "warning", "แจ้งเตือน", result?.message || "บันทึกไม่สำเร็จ");
  };

  const handleRename = async (stamp) => {
    const next = window.prompt("ชื่อตราใหม่", stamp.name);
    if (next === null || !next.trim() || next.trim() === stamp.name) return;
    loading(true);
    const result = await PUT(`${API}/stamps/${stamp.uid}`, { name: next.trim() });
    loading(false);
    if (result?.status) await onChanged();
    else await alert("", "warning", "แจ้งเตือน", result?.message || "บันทึกไม่สำเร็จ");
  };

  const handleDelete = async (stamp) => {
    if (!(await alert("", "warning", "ลบตรา", `ต้องการลบตรา "${stamp.name}"? สัญญาที่ประทับไว้แล้วไม่ได้รับผลกระทบ แต่จะวางตรานี้เพิ่มไม่ได้อีก`, "ลบ", "ยกเลิก"))) return;
    loading(true);
    const result = await DELETE(`${API}/stamps/${stamp.uid}`);
    loading(false);
    if (result?.status) await onChanged();
    else await alert("", "warning", "แจ้งเตือน", result?.message || "ลบไม่สำเร็จ");
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg" contentClassName="border-0 shadow-lg overflow-hidden dc-form-modal" dialogClassName="modal-dialog-scrollable">
      <Modal.Header closeButton className="border-0 px-4 pt-4 pb-3" style={{ background: "linear-gradient(135deg, #e8f3ff 0%, #f6fbff 100%)" }}>
        <div>
          <Modal.Title className="fw-bold mb-0" style={{ color: "#0d2a41", fontSize: "1.3rem" }}>
            <i className="fa-solid fa-stamp me-2" style={{ color: "#0c76e5" }} />จัดการตราปั๊ม
          </Modal.Title>
          <p className="mb-0 mt-1" style={{ fontSize: 13, color: "#6b8198" }}>ภาพตราบริษัทที่ใช้ประทับลงสัญญา อัปโหลดภาพชนิดใดก็ได้ ระบบแปลงเป็น PNG และลบพื้นขาวให้โปร่งใสให้อัตโนมัติ</p>
        </div>
      </Modal.Header>

      <Modal.Body className="p-4" style={{ background: "linear-gradient(180deg, #f7fbff 0%, #eef6fd 100%)" }}>
        <div className="dc-form-card mb-3">
          <h6 className="fw-bold mb-3" style={{ color: "#12314a", fontSize: 14 }}>เพิ่มตราใหม่</h6>
          <div className="row g-3 align-items-start">
            <div className="col-md-4">
              <div
                className="dc-stamp-upload"
                role="button"
                tabIndex={0}
                onClick={() => inputRef.current?.click()}
                onKeyDown={(event) => (event.key === "Enter" || event.key === " ") && inputRef.current?.click()}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  pickFile(event.dataTransfer?.files?.[0]);
                }}
              >
                <input
                  ref={inputRef}
                  type="file"
                  accept="image/*"
                  className="d-none"
                  onChange={(event) => {
                    pickFile(event.target.files?.[0]);
                    event.target.value = "";
                  }}
                />
                {converting ? (
                  <div className="text-center text-secondary" style={{ fontSize: 12.5 }}>
                    <div className="spinner-border spinner-border-sm d-block mx-auto mb-1" role="status" />
                    กำลังแปลงเป็น PNG...
                  </div>
                ) : preview ? (
                  <img src={preview} alt="ตัวอย่างตรา" />
                ) : (
                  <div className="text-center text-secondary" style={{ fontSize: 12.5 }}>
                    <i className="fa-solid fa-image d-block mb-1" style={{ fontSize: 24 }} />
                    เลือกภาพตรา (ทุกชนิด)
                  </div>
                )}
              </div>
            </div>
            <div className="col-md-8">
              <label className="dc-label">ชื่อตรา{required}</label>
              <input type="text" className="form-control dc-input" value={name} onChange={(event) => setName(event.target.value)} placeholder="เช่น ตราบริษัท, ตราสำเนาถูกต้อง" />
              <p className="dc-hint">
                {file
                  ? `${file.name} · ${formatBytes(file.size)} → จะบันทึกเป็น PNG${preview ? ` · ${formatBytes(dataUrlBytes(preview))}` : ""}`
                  : `รับภาพทุกชนิด (PNG, JPEG, WebP ฯลฯ) ไม่เกิน ${MAX_STAMP_MB}MB ระบบแปลงเป็น PNG ให้อัตโนมัติ`}
              </p>
              <div className="form-check form-switch mb-2">
                <input className="form-check-input" type="checkbox" role="switch" id="dc-stamp-remove-bg" checked={removeBg} onChange={(event) => setRemoveBg(event.target.checked)} />
                <label className="form-check-label" htmlFor="dc-stamp-remove-bg" style={{ fontSize: 13, color: "#12314a" }}>
                  ลบพื้นหลังสีขาวให้โปร่งใส
                  <span className="d-block" style={{ fontSize: 11.5, color: "#93a7b8" }}>เหมาะกับตราที่สแกน/ถ่ายบนกระดาษขาว ตราจะซ้อนบนเอกสารได้เหมือนปั๊มจริง ดูผลได้จากตัวอย่างซ้ายมือ</span>
                </label>
              </div>
              <button type="button" className="btn btn-primary px-4 mt-1" style={{ borderRadius: 12 }} onClick={handleUpload} disabled={converting}>
                <i className="fa-solid fa-cloud-arrow-up me-1" />อัปโหลดตรา
              </button>
            </div>
          </div>
        </div>

        <div className="dc-form-card">
          <h6 className="fw-bold mb-3" style={{ color: "#12314a", fontSize: 14 }}>ตราในระบบ ({stamps.length})</h6>
          {stamps.length ? (
            <div className="row g-3">
              {stamps.map((stamp) => (
                <div className="col-6 col-md-4" key={stamp.uid}>
                  <div className={`dc-stamp-card${stamp.is_default ? " dc-stamp-card-default" : ""}`}>
                    <div className="dc-stamp-card-thumb">
                      {stampImages[stamp.uid] ? <img src={stampImages[stamp.uid]} alt={stamp.name} /> : <div className="spinner-border spinner-border-sm text-secondary" role="status" />}
                    </div>
                    <div className="px-2 pt-2">
                      <div className="d-flex align-items-center gap-1">
                        <span className="fw-semibold text-truncate" style={{ fontSize: 13, color: "#12314a" }}>{stamp.name}</span>
                        {stamp.is_default ? <span className="dc-pill ms-auto flex-shrink-0">ค่าเริ่มต้น</span> : null}
                      </div>
                      <div className="small text-secondary">{formatBytes(stamp.file_size)}</div>
                    </div>
                    <div className="d-flex gap-1 p-2">
                      {!stamp.is_default && (
                        <button type="button" className="btn btn-sm btn-outline-primary rounded-3 flex-grow-1" style={{ fontSize: 12 }} onClick={() => handleSetDefault(stamp)}>
                          ตั้งเป็นค่าเริ่มต้น
                        </button>
                      )}
                      <button type="button" className="btn btn-sm btn-outline-secondary rounded-3" title="เปลี่ยนชื่อ" onClick={() => handleRename(stamp)}>
                        <i className="fa-solid fa-pen" />
                      </button>
                      <button type="button" className="btn btn-sm btn-outline-danger rounded-3" title="ลบ" onClick={() => handleDelete(stamp)}>
                        <i className="fa-solid fa-trash" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-secondary py-3" style={{ fontSize: 13 }}>ยังไม่มีตราในระบบ — อัปโหลดภาพตราบริษัทด้านบนเพื่อเริ่มใช้งาน</div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className="border-0 px-4 py-3" style={{ background: "#ffffff", borderTop: "1px solid rgba(18, 125, 255, 0.12)" }}>
        <button type="button" className="btn btn-light px-4" style={{ borderRadius: 12, border: "1px solid rgba(18, 125, 255, 0.14)", color: "#4f6d83" }} onClick={onHide}>
          ปิด
        </button>
      </Modal.Footer>
    </Modal>
  );
};

/* ------------------------------------------------------------------ */
/* หน้าหลัก                                                            */
/* ------------------------------------------------------------------ */

export default function DevContracts() {
  const [data, setData] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [stampFilter, setStampFilter] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [showStamps, setShowStamps] = useState(false);
  const [editorItem, setEditorItem] = useState(null);

  const [stamps, setStamps] = useState([]);
  const [stampImages, setStampImages] = useState({});
  // แคชภาพตรา (data URL) ไว้ตลอดอายุหน้า ภาพเล็กและไม่ค่อยเปลี่ยน ไม่ต้องดึงซ้ำทุกครั้งที่เปิดตัวแก้ไข
  const imageCacheRef = useRef({});

  const getData = useCallback(async () => {
    loading(true);
    const result = await GET(API, { page: 0 });
    if (result?.status) setData(result.data || []);
    loading(false);
  }, []);

  const loadStamps = useCallback(async () => {
    const result = await GET(`${API}/stamps`);
    if (!result?.status) return;
    const list = result.data || [];
    const missing = list.filter((stamp) => !imageCacheRef.current[stamp.uid]);
    await Promise.all(
      missing.map(async (stamp) => {
        const image = await GET(`${API}/stamps/${stamp.uid}/image`);
        if (image?.status && image.data?.data_url) imageCacheRef.current[stamp.uid] = image.data.data_url;
      })
    );
    // ตัดภาพของตราที่ถูกลบไปแล้วออกจากแคช
    const next = {};
    list.forEach((stamp) => {
      if (imageCacheRef.current[stamp.uid]) next[stamp.uid] = imageCacheRef.current[stamp.uid];
    });
    imageCacheRef.current = next;
    setStampImages(next);
    setStamps(list);
  }, []);

  useEffect(() => {
    if (!isSuperUser) return;
    getData();
    loadStamps();
  }, [getData, loadStamps]);

  const visibleRows = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return data.filter((item) => {
      if (keyword) {
        const haystack = [item.project_name, item.customer_name, item.contract_no, item.original_file_name].filter(Boolean).join(" ").toLowerCase();
        if (!haystack.includes(keyword)) return false;
      }
      if (stampFilter === "stamped" && !item.has_stamped) return false;
      if (stampFilter === "pending" && item.has_stamped) return false;
      return true;
    });
  }, [data, search, stampFilter]);

  const stats = useMemo(() => {
    const stamped = data.filter((item) => item.has_stamped).length;
    return {
      total: data.length,
      stamped,
      pending: data.length - stamped,
      amount: data.reduce((sum, item) => sum + (Number(item.contract_amount) || 0), 0),
    };
  }, [data]);

  const summaryCards = [
    { label: "สัญญาทั้งหมด", value: stats.total, icon: "fa-file-contract", color: "#0f6bdc" },
    { label: "ประทับตราแล้ว", value: stats.stamped, icon: "fa-stamp", color: "#10b981" },
    { label: "รอประทับตรา", value: stats.pending, icon: "fa-hourglass-half", color: "#f59e0b", glow: stats.pending > 0 },
    { label: "มูลค่ารวม", value: money(stats.amount), icon: "fa-coins", color: "#7c3aed" },
  ];

  /* ---- actions ---- */
  const openCreate = () => {
    setEditData(null);
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditData(item);
    setShowForm(true);
  };

  // เปิดตัวแก้ PDF กลาง (คลังตรา/ลายเซ็นมาจาก drive assets) ตราเดิมของสัญญาใช้แปลง layout แบบเก่าเท่านั้น
  const openEditor = (item) => setEditorItem(item);

  const handleSave = async (form, file) => {
    if (!form.project_name?.trim()) return alert("", "warning", "แจ้งเตือน", "กรุณากรอกชื่อโปรเจกต์ / ระบบ");
    if (!form.uid && !file) return alert("", "warning", "แจ้งเตือน", "กรุณาเลือกไฟล์ PDF สัญญาต้นฉบับ");
    if (form.start_date && form.end_date && form.end_date < form.start_date) return alert("", "warning", "แจ้งเตือน", "วันส่งมอบต้องไม่ก่อนวันเริ่มงาน");
    if (String(form.contract_amount).trim() !== "" && (!Number.isFinite(Number(form.contract_amount)) || Number(form.contract_amount) < 0))
      return alert("", "warning", "แจ้งเตือน", "มูลค่าสัญญาต้องเป็นตัวเลขและไม่ติดลบ");
    const replacing = !!(form.uid && file);
    if (!(await alert("", "info", "ยืนยัน", replacing ? "เปลี่ยนไฟล์ต้นฉบับ? ฉบับประทับตราเดิมจะถูกลบและต้องประทับใหม่" : "ต้องการบันทึกสัญญานี้?", "บันทึก", "ยกเลิก"))) return undefined;

    loading(true);
    const body = {
      project_name: form.project_name.trim(),
      customer_name: form.customer_name,
      contract_no: form.contract_no,
      contract_date: form.contract_date,
      start_date: form.start_date,
      end_date: form.end_date,
      contract_amount: String(form.contract_amount).trim() === "" ? undefined : Number(form.contract_amount),
      note: form.note,
    };
    if (file) {
      body.file = { file_name: file.name, mime_type: file.type || "application/pdf", file_base64: await readFileAsDataUrl(file) };
    }

    const result = form.uid
      ? await PUT(`${API}/${form.uid}`, body)
      : await POST(API, body, null, { onProgress: (percent) => loading(true, percent) });
    loading(false);

    if (result?.status) {
      setShowForm(false);
      setEditData(null);
      await alert("", "success", "สำเร็จ", form.uid ? "บันทึกการแก้ไขเรียบร้อย" : "นำเข้าสัญญาเรียบร้อย");
      getData();
    } else {
      await alert("", "warning", "แจ้งเตือน", result?.message || "บันทึกไม่สำเร็จ");
    }
    return undefined;
  };

  const handleDelete = async (item) => {
    if (!(await alert("", "warning", "ลบสัญญา", `ต้องการลบสัญญา "${item.project_name}" ทั้งต้นฉบับและฉบับประทับตรา?`, "ลบ", "ยกเลิก"))) return;
    loading(true);
    const result = await DELETE(`${API}/${item.uid}`);
    loading(false);
    if (result?.status) {
      await alert("", "success", "สำเร็จ", "ลบสัญญาเรียบร้อย");
      getData();
    } else {
      await alert("", "warning", "แจ้งเตือน", result?.message || "ลบไม่สำเร็จ");
    }
  };

  const handleRemoveStamped = async (item) => {
    if (!(await alert("", "warning", "ลบฉบับประทับตรา", `ต้องการลบ PDF ฉบับประทับตราของ "${item.project_name}"? ต้นฉบับและตำแหน่งตราที่วางไว้ยังอยู่`, "ลบ", "ยกเลิก"))) return;
    loading(true);
    const result = await DELETE(`${API}/${item.uid}/stamped`);
    loading(false);
    if (result?.status) getData();
    else await alert("", "warning", "แจ้งเตือน", result?.message || "ลบไม่สำเร็จ");
  };

  // เปิดดู/ดาวน์โหลดผ่าน signed url (หมดอายุใน 15 นาที)
  const openFile = async (item, kind, disposition = "inline") => {
    const result = await GET(`${API}/${item.uid}/file-url`, { kind, disposition });
    if (result?.status && result.data?.url) window.open(result.data.url, "_blank", "noopener");
    else await alert("", "warning", "แจ้งเตือน", result?.message || "เปิดไฟล์ไม่สำเร็จ");
  };

  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setStampFilter("");
  };

  if (!isSuperUser) {
    return (
      <AppShell title={PAGE_TITLE} description="เอกสารสัญญาพัฒนาระบบของบริษัท">
        <div className="p-5 text-center" style={sectionCardStyle}>
          <i className="fa-solid fa-lock mb-3" style={{ fontSize: 40, color: "#94a3b8" }} />
          <h5 className="fw-bold" style={{ color: "#12314a" }}>เฉพาะผู้ดูแลระบบสูงสุด (Super User)</h5>
          <p className="text-secondary mb-0">หน้านี้เปิดให้เฉพาะบัญชีประเภท super_user เท่านั้น</p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell title={PAGE_TITLE} description="นำเข้า PDF สัญญาต้นฉบับ ประทับตราบริษัทลงบนเอกสาร และเก็บฉบับประทับตราไว้ในระบบ">
      {/* ---- KPI ---- */}
      <div className="row g-3 mb-3">
        {summaryCards.map((card) => (
          <div className="col-6 col-xl-3" key={card.label}>
            <div
              className={`h-100 rounded-4 p-3 p-md-4 d-flex align-items-center gap-3 dc-kpi${card.glow ? " dc-kpi-glow" : ""}`}
              style={{
                background: "linear-gradient(180deg, #ffffff 0%, #f2f9ff 100%)",
                border: `1px solid ${card.glow ? `${card.color}66` : "rgba(18, 125, 255, 0.10)"}`,
                boxShadow: "0 14px 28px rgba(12, 118, 229, 0.06)",
                "--dc-glow": `${card.color}59`,
              }}
            >
              <div className="d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 44, height: 44, borderRadius: 12, background: `${card.color}1f`, color: card.color, fontSize: 18 }}>
                <i className={`fa-solid ${card.icon}`} />
              </div>
              <div style={{ minWidth: 0 }}>
                <p className="small text-secondary mb-1 text-truncate">{card.label}</p>
                <h3 className="h4 mb-0 fw-bold text-truncate" style={{ color: card.color }}>{card.value}</h3>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ---- Filters ---- */}
      <div className="d-flex flex-column flex-xl-row justify-content-between align-items-xl-center mb-3 gap-3">
        <div className="d-flex gap-2 flex-wrap align-items-center filters-mobile-stack">
          <input
            type="text"
            className="form-control"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && setSearch(searchInput)}
            placeholder="ค้นหาโปรเจกต์ ลูกค้า เลขที่สัญญา..."
            style={{ width: 240, borderRadius: 12 }}
          />
          <div style={{ width: 190 }}>
            <Select className="form-control" value={stampFilter} options={STAMP_FILTER_OPTIONS} placeholder="ทุกสถานะ" onChange={(value) => setStampFilter(value)} />
          </div>
          <button type="button" className="btn btn-primary px-4" onClick={() => setSearch(searchInput)} style={{ borderRadius: 12 }}>ค้นหา</button>
          <button type="button" className="btn btn-light px-4" onClick={clearFilters} style={{ borderRadius: 12, border: "1px solid rgba(18, 125, 255, 0.14)", color: "#4f6d83" }}>ล้างค่า</button>
        </div>
        <div className="d-flex gap-2 flex-shrink-0">
          <button type="button" className="btn btn-light px-3" style={{ borderRadius: 12, border: "1px solid rgba(18, 125, 255, 0.14)", color: "#0b4f84" }} onClick={() => setShowStamps(true)}>
            <i className="fa-solid fa-stamp me-1" />จัดการตราปั๊ม
            <span className="ms-2 rounded-pill px-2 fw-bold" style={{ fontSize: 11, background: "rgba(12, 117, 230, 0.12)", color: "#0b4f84" }}>{stamps.length}</span>
          </button>
          <button type="button" className="btn btn-primary px-4" style={{ borderRadius: 12 }} onClick={openCreate}>
            <i className="fa-solid fa-file-import me-1" />นำเข้าสัญญา
          </button>
        </div>
      </div>

      {/* ---- Table ---- */}
      <div className="p-2 p-md-3" style={sectionCardStyle}>
        <div className="table-responsive table-mobile-cards">
          <table className="table align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th className="text-secondary fw-medium">โปรเจกต์ / สัญญา</th>
                <th className="text-secondary fw-medium">ลูกค้า / คู่สัญญา</th>
                <th className="text-secondary fw-medium text-nowrap">ไฟล์ต้นฉบับ</th>
                <th className="text-secondary fw-medium text-end text-nowrap">มูลค่า (บาท)</th>
                <th className="text-secondary fw-medium text-center">สถานะตรา</th>
                <th className="text-secondary fw-medium text-center">จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.length > 0 ? (
                visibleRows.map((item) => (
                  <tr key={item.uid} className="dc-row">
                    <td data-label="โปรเจกต์" style={{ borderLeft: `4px solid ${item.has_stamped ? "#10b981" : "#f59e0b"}` }}>
                      <div className="fw-semibold text-truncate" style={{ color: "#132f46" }}>{item.project_name}</div>
                      <div className="small text-secondary d-flex flex-wrap align-items-center gap-2 mt-1">
                        {item.contract_no ? (
                          <span className="rounded-pill px-2 fw-bold" style={{ fontSize: 10.5, lineHeight: "18px", background: "rgba(12, 117, 230, 0.12)", color: "#0b4f84" }}>{item.contract_no}</span>
                        ) : null}
                        <span>ทำสัญญา {showDate(item.contract_date)}</span>
                        {item.start_date || item.end_date ? <span>· {showDate(item.start_date)} → {showDate(item.end_date)}</span> : null}
                      </div>
                    </td>
                    <td data-label="ลูกค้า">
                      <div className="fw-medium text-truncate">{item.customer_name || "—"}</div>
                      {item.note ? <div className="small text-secondary text-truncate" style={{ maxWidth: 260 }}>{item.note}</div> : null}
                    </td>
                    <td data-label="ไฟล์ต้นฉบับ">
                      <button type="button" className="btn btn-link p-0 text-start text-truncate d-block" style={{ fontSize: 13, maxWidth: 220, textDecoration: "none", color: "#0c76e5" }} title="เปิดดูต้นฉบับ" onClick={() => openFile(item, "original")}>
                        <i className="fa-solid fa-file-pdf me-1" style={{ color: "#dc2626" }} />{item.original_file_name || "ต้นฉบับ.pdf"}
                      </button>
                      <div className="small text-secondary">
                        {formatBytes(item.original_file_size)}
                        {item.page_count ? ` · ${item.page_count} หน้า` : ""}
                      </div>
                    </td>
                    <td data-label="มูลค่า" className="text-end text-nowrap">
                      <div className="fw-bold" style={{ color: "#132f46" }}>{money(item.contract_amount)}</div>
                    </td>
                    <td data-label="สถานะตรา" className="text-center text-nowrap">
                      {item.has_stamped ? (
                        <>
                          <span className="rounded-pill px-3 py-1 small fw-semibold d-inline-block" style={{ background: "rgba(16, 185, 129, 0.14)", color: "#047857" }}>
                            <i className="fa-solid fa-circle-check me-1" />ประทับตราแล้ว
                          </span>
                          <div className="small text-secondary mt-1">{item.stamped_at || ""}</div>
                        </>
                      ) : (
                        <span className="rounded-pill px-3 py-1 small fw-semibold d-inline-block dc-pulse" style={{ background: "rgba(245, 158, 11, 0.18)", color: "#b45309" }}>
                          <i className="fa-solid fa-hourglass-half me-1" />รอประทับตรา
                        </span>
                      )}
                    </td>
                    <td data-label="จัดการ" className="text-center text-nowrap">
                      <button className="btn btn-sm btn-primary rounded-3 me-1" title="แก้ไข PDF / ประทับตรา" onClick={() => openEditor(item)}>
                        <i className="fa-solid fa-file-pen me-1" />แก้ไข PDF
                      </button>
                      {item.has_stamped ? (
                        <>
                          <button className="btn btn-sm btn-outline-success rounded-3 me-1" title="เปิดดูฉบับประทับตรา" onClick={() => openFile(item, "stamped")}>
                            <i className="fa-solid fa-eye" />
                          </button>
                          <button className="btn btn-sm btn-outline-success rounded-3 me-1" title="ดาวน์โหลดฉบับประทับตรา" onClick={() => openFile(item, "stamped", "attachment")}>
                            <i className="fa-solid fa-download" />
                          </button>
                          <button className="btn btn-sm btn-outline-warning rounded-3 me-1" title="ลบฉบับประทับตรา (เก็บต้นฉบับไว้)" onClick={() => handleRemoveStamped(item)}>
                            <i className="fa-solid fa-eraser" />
                          </button>
                        </>
                      ) : (
                        <button className="btn btn-sm btn-outline-secondary rounded-3 me-1" title="ดาวน์โหลดต้นฉบับ" onClick={() => openFile(item, "original", "attachment")}>
                          <i className="fa-solid fa-download" />
                        </button>
                      )}
                      <button className="btn btn-sm btn-outline-secondary rounded-3 me-1" title="แก้ไขรายละเอียด / เปลี่ยนไฟล์" onClick={() => openEdit(item)}>
                        <i className="fa-solid fa-pen" />
                      </button>
                      <button className="btn btn-sm btn-outline-danger rounded-3" title="ลบสัญญา" onClick={() => handleDelete(item)}>
                        <i className="fa-solid fa-trash" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center text-secondary py-5">
                    {data.length ? "ไม่พบสัญญาที่ตรงกับเงื่อนไขที่เลือก" : "ยังไม่มีสัญญา — กด “นำเข้าสัญญา” เพื่ออัปโหลด PDF ต้นฉบับ"}
                  </td>
                </tr>
              )}
            </tbody>
            {visibleRows.length > 0 && (
              <tfoot>
                <tr style={{ background: "#f3f9ff" }}>
                  <td colSpan="3" className="fw-semibold text-end" style={{ color: "#0b4f84" }}>รวม {visibleRows.length} สัญญา</td>
                  <td className="text-end fw-bold" style={{ color: "#0b4f84" }}>{money(visibleRows.reduce((sum, item) => sum + (Number(item.contract_amount) || 0), 0))}</td>
                  <td colSpan="2" />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <ContractModal
        show={showForm}
        onHide={() => {
          setShowForm(false);
          setEditData(null);
        }}
        editData={editData}
        onSave={handleSave}
      />

      <StampManagerModal show={showStamps} onHide={() => setShowStamps(false)} stamps={stamps} stampImages={stampImages} onChanged={loadStamps} />

      {editorItem ? (
        <PdfEditor
          key={editorItem.uid}
          source={devContractSource({ contract: editorItem, stampImages })}
          onClose={() => setEditorItem(null)}
          onSaved={getData}
        />
      ) : null}

      <style>{`
        .dc-row { transition: filter .15s ease; }
        .dc-row:hover { filter: brightness(0.97); }
        .dc-pulse { animation: dc-pulse 1.8s ease-in-out infinite; }
        @keyframes dc-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.35); }
          50% { box-shadow: 0 0 0 5px rgba(245, 158, 11, 0); }
        }
        .dc-kpi { transition: transform .15s ease, box-shadow .15s ease; }
        .dc-kpi:hover { transform: translateY(-3px); }
        .dc-kpi-glow { animation: dc-glow 2.2s ease-in-out infinite; }
        @keyframes dc-glow {
          0%, 100% { box-shadow: 0 14px 28px rgba(12, 118, 229, 0.06); }
          50% { box-shadow: 0 14px 28px rgba(12, 118, 229, 0.06), 0 0 0 5px var(--dc-glow); }
        }

        .dc-form-card {
          background: #ffffff;
          border: 1px solid rgba(18, 125, 255, 0.10);
          border-radius: 18px;
          padding: 22px 22px 24px;
          box-shadow: 0 2px 10px rgba(15, 40, 70, 0.04);
        }
        .dc-form-card + .dc-form-card { margin-top: 18px; }
        .dc-form-step {
          flex-shrink: 0; width: 30px; height: 30px; border-radius: 10px;
          display: inline-flex; align-items: center; justify-content: center;
          font-size: 13px; font-weight: 700; color: #0c76e5;
          background: rgba(12, 118, 229, 0.10); border: 1px solid rgba(12, 118, 229, 0.18);
        }
        .dc-label { display: block; font-size: 12.5px; font-weight: 600; color: #56718a; margin-bottom: 7px; letter-spacing: 0.01em; }
        .dc-hint { margin: 6px 0 0; font-size: 11.5px; color: #93a7b8; line-height: 1.45; }
        .dc-form-modal .dc-input {
          border-radius: 12px; border: 1px solid #dbe5ee; background: #fbfdff; color: #17364d;
          font-size: 14px; padding: 10px 14px; box-shadow: none;
          transition: border-color .15s ease, box-shadow .15s ease, background .15s ease;
        }
        .dc-form-modal textarea.dc-input { line-height: 1.6; }
        .dc-form-modal .dc-input::placeholder { color: #adbecd; }
        .dc-form-modal .dc-input:hover { border-color: #c4d6e6; }
        .dc-form-modal .dc-input:focus, .dc-form-modal .dc-input:focus-within {
          background: #ffffff; border-color: #0c76e5; box-shadow: 0 0 0 4px rgba(12, 118, 229, 0.12);
        }

        .dc-dropzone {
          border: 2px dashed #c4d6e6; border-radius: 16px; padding: 26px 18px; text-align: center; cursor: pointer;
          background: #fbfdff; transition: border-color .15s ease, background .15s ease;
        }
        .dc-dropzone:hover, .dc-dropzone-active { border-color: #0c76e5; background: rgba(12, 118, 229, 0.05); }

        .dc-stamp-upload {
          height: 150px; border: 2px dashed #c4d6e6; border-radius: 14px; display: flex; align-items: center; justify-content: center;
          cursor: pointer; overflow: hidden; padding: 8px;
          background-color: #ffffff;
          background-image: linear-gradient(45deg, #eef2f6 25%, transparent 25%), linear-gradient(-45deg, #eef2f6 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #eef2f6 75%), linear-gradient(-45deg, transparent 75%, #eef2f6 75%);
          background-size: 14px 14px; background-position: 0 0, 0 7px, 7px -7px, -7px 0;
        }
        .dc-stamp-upload:hover { border-color: #0c76e5; }
        .dc-stamp-upload img { max-width: 100%; max-height: 100%; object-fit: contain; }

        .dc-stamp-card { border: 1px solid #dbe5ee; border-radius: 14px; background: #ffffff; overflow: hidden; }
        .dc-stamp-card-default { border-color: rgba(12, 118, 229, 0.45); box-shadow: 0 0 0 3px rgba(12, 118, 229, 0.08); }
        .dc-stamp-card-thumb {
          height: 110px; display: flex; align-items: center; justify-content: center; padding: 10px;
          background-color: #ffffff;
          background-image: linear-gradient(45deg, #eef2f6 25%, transparent 25%), linear-gradient(-45deg, #eef2f6 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #eef2f6 75%), linear-gradient(-45deg, transparent 75%, #eef2f6 75%);
          background-size: 14px 14px; background-position: 0 0, 0 7px, 7px -7px, -7px 0;
        }
        .dc-stamp-card-thumb img { max-width: 100%; max-height: 100%; object-fit: contain; }
        .dc-pill { font-size: 10px; font-weight: 700; padding: 1px 7px; border-radius: 999px; background: rgba(12, 118, 229, 0.12); color: #0b4f84; }

        @media (prefers-reduced-motion: reduce) {
          .dc-pulse, .dc-kpi-glow { animation: none !important; }
        }
        @media (max-width: 767px) {
          .dc-row td:first-child { border-left-width: 6px !important; }
        }
      `}</style>
    </AppShell>
  );
}
